import { home } from "@/content";
import { Rich } from "@/lib/rich";

export default function LightPricing() {
  const { pricing: section } = home;

  return (
    <section id="pricing" className="py-24 bg-[#FAFAF8] dark:bg-[#0A0A0A] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-stone-100 dark:bg-white/[0.06] text-stone-600 dark:text-white/70 text-xs font-semibold tracking-widest uppercase mb-4">
            {section.badge}
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-stone-900 dark:text-white mb-4">
            <Rich text={section.title} accentClassName="text-orange-700 dark:text-orange-400" />
          </h2>
          {section.subtitle && (
            <p className="text-stone-600 dark:text-white/65 text-lg">{section.subtitle}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          {section.plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-3xl p-8 transition-all duration-300 ${
                plan.highlight
                  ? "bg-stone-900 dark:bg-white/[0.06] text-white shadow-2xl dark:backdrop-blur-xl dark:ring-1 dark:ring-orange-500/20 scale-[1.02]"
                  : "bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/[0.08] dark:backdrop-blur-xl text-stone-900 dark:text-white hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
              }`}
            >
              {plan.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-orange-600 dark:bg-orange-500 text-white dark:text-[#0A0A0A] text-xs font-bold whitespace-nowrap shadow-lg">
                  {section.popularLabel}
                </div>
              )}

              <div
                className={`text-sm font-semibold mb-1 ${
                  plan.highlight ? "text-white/60" : "text-stone-600 dark:text-white/65"
                }`}
              >
                {plan.description}
              </div>
              <div
                className={`text-xl font-bold mb-2 ${
                  plan.highlight ? "text-white" : "text-stone-900 dark:text-white"
                }`}
              >
                {plan.name}
              </div>
              <div className="flex items-end gap-1 mb-6">
                <span
                  className={`text-3xl font-extrabold ${
                    plan.highlight ? "text-white" : "text-stone-900 dark:text-white"
                  }`}
                >
                  {plan.price} ₽
                </span>
                <span
                  className={`text-sm mb-1 ${
                    plan.highlight ? "text-white/50" : "text-stone-600 dark:text-white/65"
                  }`}
                >
                  /{plan.period || section.perPeriod}
                </span>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2.5 text-sm">
                    <div
                      className={`w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 ${
                        plan.highlight ? "bg-white/20" : "bg-stone-100 dark:bg-white/[0.06]"
                      }`}
                    >
                      <i
                        className={`ri-check-line text-xs ${
                          plan.highlight ? "text-white" : "text-stone-700 dark:text-white/60"
                        }`}
                      ></i>
                    </div>
                    <span
                      className={
                        plan.highlight ? "text-white/80" : "text-stone-600 dark:text-white/65"
                      }
                    >
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                className={`block text-center py-3.5 rounded-2xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                  plan.highlight
                    ? "bg-white dark:bg-orange-500 text-stone-900 dark:text-[#0A0A0A] hover:bg-stone-100 dark:hover:bg-orange-400"
                    : "border border-stone-200 dark:border-white/20 text-stone-700 dark:text-white hover:border-stone-400 dark:hover:border-white/30 hover:bg-stone-50 dark:hover:bg-white/[0.06]"
                }`}
              >
                {plan.cta}
              </a>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-stone-600 dark:text-white/65 text-sm">
            {section.footnote.text}{" "}
            <a
              href={section.footnote.linkHref}
              className="text-stone-700 dark:text-white font-semibold hover:underline cursor-pointer"
            >
              {section.footnote.linkLabel}
            </a>{" "}
            {section.footnote.after}
          </p>
        </div>
      </div>
    </section>
  );
}
